import { useState } from "react";
import { channelTabs, heatBoard, rankExtra } from "@/mocks/leakData";

export default function HeatBoard() {
  const [active, setActive] = useState("hot");

  return (
    <section id="heat" className="relative overflow-hidden bg-background-100">
      <div className="bg-paper pointer-events-none absolute inset-0 opacity-40" />
      <div className="relative mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        {/* 栏目头 */}
        <div className="mb-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.28em] text-primary-600">
              <i className="ri-fire-line" />
              Hot Board
            </span>
            <h2 className="mt-2 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
              热度沸点榜 <span className="text-primary-600">TOP</span>
            </h2>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-secondary-600">
              按实时讨论、分享与核验进度综合排序，榜单每 30 分钟刷新一次。
            </p>
          </div>
          {/* 分段切换器 */}
          <div className="inline-flex rounded-full bg-background-200/80 p-1">
            {channelTabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition ${
                  active === t.id
                    ? "bg-primary-600 text-background-50"
                    : "text-secondary-600 hover:text-foreground-900"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* 左：榜首巨型卡 */}
          <div className="lg:col-span-5">
            <a
              href="#express"
              className="group relative block h-full overflow-hidden rounded-xl bg-foreground-950"
            >
              <img
                src="https://readdy.ai/api/search-image?query=secretive%20deal%20meeting%20with%20lawyers%20signing%20contract%20in%20shadow%2C%20piles%20of%20documents%20and%20seal%2C%20dramatic%20crimson%20amber%20lighting%2C%20high%20contrast%20investigative%20business%20editorial%20photography%2C%20vertical%20composition&width=900&height=1000&seq=hot-top-01&orientation=portrait"
                alt={heatBoard[0].title}
                className="absolute inset-0 h-full w-full object-cover object-top opacity-65 transition duration-500 group-hover:opacity-50"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
              <div className="relative flex min-h-[380px] flex-col justify-end p-6 md:min-h-[460px]">
                <span className="rib-text font-heading text-8xl font-black leading-none md:text-9xl">
                  {String(heatBoard[0].id).padStart(2, "0")}
                </span>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="rounded-sm bg-primary-500 px-2 py-0.5 text-[11px] font-semibold text-background-50">
                    {heatBoard[0].tag}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[11px] text-background-100/70">
                    <i className="ri-verified-badge-fill text-accent-400" />
                    已核验 · {heatBoard[0].src}
                  </span>
                </div>
                <h3 className="mt-3 font-heading text-2xl font-black leading-snug text-background-50 md:text-3xl">
                  {heatBoard[0].title}
                </h3>
                <div className="mt-4 flex items-center gap-5 text-sm">
                  <span className="inline-flex items-center gap-1.5 text-accent-400">
                    <i className="ri-fire-fill" />
                    <b className="font-heading text-2xl">{heatBoard[0].heat}</b>
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-accent-500/20 px-2.5 py-1 text-xs font-semibold text-accent-300">
                    <i className="ri-arrow-up-line" />
                    {heatBoard[0].delta}
                  </span>
                  <span className="ml-auto inline-flex items-center gap-1 text-xs text-background-100/70 transition group-hover:text-accent-300">
                    立即查看 <i className="ri-arrow-right-line" />
                  </span>
                </div>
              </div>
            </a>
          </div>

          {/* 右：榜单列表 */}
          <div className="flex flex-col gap-3 lg:col-span-7">
            {heatBoard.slice(1).map((item, idx) => (
              <a
                key={item.id}
                href="#express"
                className="group flex items-center gap-4 rounded-lg border border-background-200/80 bg-background-50 p-4 transition hover:border-primary-200 hover:bg-primary-50/40 md:gap-5"
              >
                <span
                  className={`font-heading text-3xl font-black leading-none md:text-4xl ${
                    idx === 0 ? "text-primary-500" : idx === 1 ? "text-accent-600" : "text-secondary-300"
                  }`}
                >
                  {String(item.id).padStart(2, "0")}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span className="rounded-sm bg-background-200 px-1.5 py-0.5 text-[11px] font-medium text-secondary-600">
                      {item.tag}
                    </span>
                    {item.verified && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-primary-600">
                        <i className="ri-verified-badge-fill" /> 已核验
                      </span>
                    )}
                  </div>
                  <h4 className="line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-900 transition group-hover:text-primary-700">
                    {item.title}
                  </h4>
                  <p className="mt-1 text-xs text-secondary-500">来源：{item.src}</p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1.5">
                  <span className="inline-flex items-center gap-1 font-heading text-lg font-black text-foreground-950">
                    <i className="ri-fire-fill text-accent-500" />
                    {item.heat}
                  </span>
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-semibold text-accent-700">
                    <i className="ri-arrow-up-line" /> {item.delta}
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* 榜单延伸行 */}
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {rankExtra.map((r) => (
            <a
              key={r.rank}
              href="#express"
              className="group flex items-center gap-3 rounded-lg border border-background-200/70 bg-background-50/70 px-4 py-3 transition hover:border-accent-200 hover:bg-accent-50"
            >
              <span className="font-heading text-xl font-black text-secondary-400">
                {String(r.rank).padStart(2, "0")}
              </span>
              <p className="line-clamp-2 flex-1 text-xs leading-relaxed text-secondary-700 group-hover:text-foreground-900">
                {r.title}
              </p>
              <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-semibold text-accent-700">
                <i className="ri-arrow-up-line" /> {r.delta}
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}